import { useState, useEffect, useCallback, useRef } from "react";
import {
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
  Phone,
  Pencil,
  Trash2,
  X,
  CalendarDays,
  RefreshCw,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import api from "../lib/api-client";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";

/**
 * Módulo Clases (add-on): agenda por sesión + configuración de plantillas.
 * Las reservas de clase son reservas normales enlazadas por classTemplateId;
 * aquí solo se pintan agrupadas por clase y se dan de alta asistentes.
 */

interface ClassTemplate {
  id: string;
  name: string;
  emoji: string | null;
  teacherName: string | null;
  durationMinutes: number;
  capacity: number;
  weekdays: number[];
  startTime: string;
  isActive: boolean;
}

interface ClassAttendee {
  id: string;
  partySize: number;
  status: string;
  source: string;
  bookingName: string | null;
  /** Empresa que reservó en nombre del cliente (hotel, agencia…). */
  bookedByCompany: string | null;
  customerId: string;
  customer: {
    firstName: string;
    lastName: string;
    phone: string | null;
  };
}

/** Reserva abierta en el modal de gestión de la agenda. */
interface ManageBookingState {
  attendee: ClassAttendee;
  templateName: string;
  startTime: string;
  date: string; // yyyy-MM-dd
}

interface ClassSession {
  template: {
    id: string;
    name: string;
    emoji: string | null;
    teacherName: string | null;
    durationMinutes: number;
    startTime: string;
  };
  capacity: number;
  booked: number;
  remaining: number;
  cancelled: boolean;
  attendees: ClassAttendee[];
}

interface GoogleStatus {
  connected: boolean;
  locationName?: string;
  googleEmail?: string | null;
  syncStatus?: string | null;
  lastError?: string | null;
  lastSyncAt?: string | null;
}

/** Resumen de un día en la pestaña Calendario (solo días con clases). */
interface CalendarDayInfo {
  date: string;
  total: number;
  cancelled: number;
  booked: number;
}

// Orden visual L→D sobre la convención getDay() (0=domingo).
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAY_SHORT: Record<number, string> = {
  0: "Dom",
  1: "Lun",
  2: "Mar",
  3: "Mié",
  4: "Jue",
  5: "Vie",
  6: "Sáb",
};

function formatWeekdays(days: number[]): string {
  return WEEKDAY_ORDER.filter((d) => days.includes(d))
    .map((d) => WEEKDAY_SHORT[d])
    .join(", ");
}

interface TemplateFormState {
  name: string;
  emoji: string;
  teacherName: string;
  durationMinutes: number;
  capacity: number;
  weekdays: number[];
  startTime: string;
  /** Vacío = clase única. Con valor = crear una clase por cada franja de
   * `durationMinutes` entre startTime y endTime (12→15 con 60 = 3 clases). */
  endTime: string;
  isActive: boolean;
}

const EMPTY_FORM: TemplateFormState = {
  name: "",
  emoji: "",
  teacherName: "",
  durationMinutes: 60,
  capacity: 20,
  weekdays: [],
  startTime: "19:00",
  endTime: "",
  isActive: true,
};

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Horas de inicio de cada franja: [inicio, inicio+dur, …] mientras la clase
 * completa quepa antes del fin. */
function slotStarts(start: string, end: string, duration: number): string[] {
  const startM = timeToMinutes(start);
  const endM = timeToMinutes(end);
  const out: string[] = [];
  for (let t = startM; t + duration <= endM; t += duration) {
    out.push(minutesToTime(t));
  }
  return out;
}

export default function ClassesPage() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  const [tab, setTab] = useState<"agenda" | "calendar" | "config">("agenda");

  // ——— Agenda ———
  const [agendaDate, setAgendaDate] = useState(() => new Date());

  // ?date=YYYY-MM-DD (p. ej. al pulsar una notificación de la campanita):
  // abrir la agenda en ese día, también si la página ya estaba montada.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const p = searchParams.get("date");
    if (p && /^\d{4}-\d{2}-\d{2}$/.test(p)) {
      setAgendaDate(new Date(`${p}T00:00:00`));
      setTab("agenda");
    }
  }, [searchParams]);
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(true);
  // Sesión con el formulario "+ Apuntar" abierto (id de plantilla)
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [bookingForm, setBookingForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    partySize: 1,
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  const dateStr = format(agendaDate, "yyyy-MM-dd");

  const fetchAgenda = useCallback(async () => {
    setAgendaLoading(true);
    try {
      const response = await api.get("/classes/agenda", {
        params: { date: dateStr },
      });
      setSessions(response.data.data || []);
    } catch {
      showToast("No se pudo cargar la agenda de clases", "error");
    } finally {
      setAgendaLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  useEffect(() => {
    fetchAgenda();
  }, [fetchAgenda]);

  // Info REAL de la semana visible para la tira (anuladas incluidas):
  // reutiliza el endpoint del calendario; si la semana cruza dos meses,
  // pide ambos. Sin esto, un día cerrado seguía saliendo como "N clases".
  const [weekInfo, setWeekInfo] = useState<Record<string, CalendarDayInfo>>(
    {},
  );
  const weekSeq = useRef(0);
  const weekMonday = addDays(agendaDate, -((agendaDate.getDay() + 6) % 7));
  const weekMonths = Array.from(
    new Set([
      format(weekMonday, "yyyy-MM"),
      format(addDays(weekMonday, 6), "yyyy-MM"),
    ]),
  ).join(",");

  const fetchWeekInfo = useCallback(async () => {
    const seq = ++weekSeq.current;
    try {
      const maps = await Promise.all(
        weekMonths.split(",").map((m) =>
          api
            .get("/classes/calendar", { params: { month: m } })
            .then((r) => r.data.data?.days || []),
        ),
      );
      if (seq !== weekSeq.current) return;
      const map: Record<string, CalendarDayInfo> = {};
      for (const days of maps) for (const d of days) map[d.date] = d;
      setWeekInfo(map);
    } catch {
      // sin datos, la tira cae al conteo local por días de la semana
    }
  }, [weekMonths]);

  useEffect(() => {
    fetchWeekInfo();
  }, [fetchWeekInfo]);

  const handleCreateBooking = async (templateId: string) => {
    if (!bookingForm.firstName.trim()) {
      showToast("El nombre es obligatorio", "error");
      return;
    }
    setBookingSubmitting(true);
    try {
      await api.post(`/classes/${templateId}/bookings`, {
        date: dateStr,
        partySize: bookingForm.partySize,
        firstName: bookingForm.firstName.trim(),
        lastName: bookingForm.lastName.trim() || null,
        phone: bookingForm.phone.trim() || null,
      });
      showToast("Asistente apuntado", "success");
      setAddingTo(null);
      setBookingForm({ firstName: "", lastName: "", phone: "", partySize: 1 });
      fetchAgenda();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      showToast(
        axiosError.response?.data?.message || "No se pudo apuntar",
        "error",
      );
    } finally {
      setBookingSubmitting(false);
    }
  };

  // ——— Configuración ———
  const [templates, setTemplates] = useState<ClassTemplate[]>([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateFormState>(EMPTY_FORM);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setConfigLoading(true);
    try {
      const response = await api.get("/classes", {
        params: { includeInactive: "true" },
      });
      setTemplates(response.data.data || []);
    } catch {
      showToast("No se pudieron cargar las clases", "error");
    } finally {
      setConfigLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Las plantillas se cargan desde el principio: la tira semanal de la
  // agenda marca los días con clase a partir de sus weekdays.
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (t: ClassTemplate) => {
    setEditingId(t.id);
    setForm({
      name: t.name,
      emoji: t.emoji || "",
      teacherName: t.teacherName || "",
      durationMinutes: t.durationMinutes,
      capacity: t.capacity,
      weekdays: t.weekdays,
      startTime: t.startTime,
      endTime: "",
      isActive: t.isActive,
    });
    setShowForm(true);
  };

  const handleSaveTemplate = async () => {
    if (!form.name.trim()) {
      showToast("El nombre es obligatorio", "error");
      return;
    }
    if (form.weekdays.length === 0) {
      showToast("Elige al menos un día de la semana", "error");
      return;
    }
    // Por franjas: hora fin → una clase por cada hueco completo de duración.
    const starts =
      !editingId && form.endTime
        ? slotStarts(form.startTime, form.endTime, form.durationMinutes)
        : [form.startTime];
    if (starts.length === 0) {
      showToast(
        "La hora de fin debe dejar sitio al menos a una clase completa",
        "error",
      );
      return;
    }

    setFormSubmitting(true);
    const basePayload = {
      name: form.name.trim(),
      teacherName: form.teacherName.trim() || null,
      durationMinutes: form.durationMinutes,
      capacity: form.capacity,
      weekdays: form.weekdays,
      isActive: form.isActive,
    };
    try {
      if (editingId) {
        await api.put(`/classes/${editingId}`, {
          ...basePayload,
          startTime: form.startTime,
        });
        showToast("Clase actualizada", "success");
      } else {
        for (const startTime of starts) {
          await api.post("/classes", { ...basePayload, startTime });
        }
        showToast(
          starts.length === 1
            ? "Clase creada"
            : `${starts.length} clases creadas (${starts.join(", ")})`,
          "success",
        );
      }
      setShowForm(false);
      fetchTemplates();
      fetchAgenda();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      // 409 al editar = cambio de horario con reservas futuras: avisar y
      // dejar forzar (el backend no mueve las reservas existentes).
      if (editingId && axiosError.response?.status === 409) {
        const ok = await confirm({
          title: "Cambiar el horario",
          message: `${axiosError.response.data?.message || "Hay reservas futuras de esta clase."} ¿Aplicar el cambio igualmente?`,
          confirmText: "Forzar el cambio",
          danger: true,
        });
        if (ok) {
          try {
            await api.put(`/classes/${editingId}`, {
              ...basePayload,
              startTime: form.startTime,
              force: true,
            });
            showToast("Clase actualizada", "success");
            setShowForm(false);
            fetchTemplates();
            fetchAgenda();
          } catch {
            showToast("No se pudo guardar la clase", "error");
          }
        }
      } else {
        showToast(
          axiosError.response?.data?.message || "No se pudo guardar la clase",
          "error",
        );
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteTemplate = async (t: ClassTemplate) => {
    const ok = await confirm({
      title: "Eliminar clase",
      message: `¿Eliminar «${t.name}»? Si tiene reservas futuras no se podrá: desactívala en su lugar.`,
      confirmText: "Eliminar",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/classes/${t.id}`);
      showToast("Clase eliminada", "success");
      fetchTemplates();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      showToast(
        axiosError.response?.data?.message || "No se pudo eliminar",
        "error",
      );
    }
  };

  const handleToggleActive = async (t: ClassTemplate) => {
    try {
      await api.put(`/classes/${t.id}`, { isActive: !t.isActive });
      showToast(
        t.isActive ? "Clase desactivada" : "Clase activada",
        "success",
      );
      fetchTemplates();
      fetchAgenda();
    } catch {
      showToast("No se pudo cambiar el estado", "error");
    }
  };

  // ——— Google Calendar (conexión de la sede) ———
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus | null>(null);
  const [googleWorking, setGoogleWorking] = useState(false);

  const fetchGoogleStatus = useCallback(async () => {
    try {
      const response = await api.get("/classes/google");
      setGoogleStatus(response.data.data || null);
    } catch {
      // silencioso: la tarjeta muestra el estado "sin conectar"
    }
  }, []);

  useEffect(() => {
    fetchGoogleStatus();
    // La autorización se hace en otra pestaña: al volver aquí, refrescar.
    const onFocus = () => fetchGoogleStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchGoogleStatus]);

  const handleGoogleConnect = async () => {
    setGoogleWorking(true);
    try {
      const response = await api.post("/classes/google/connect-url");
      const url = response.data.data?.url;
      if (url) {
        window.open(url, "_blank", "noopener");
        showToast(
          "Autoriza el acceso en la pestaña de Google y vuelve aquí",
          "info",
        );
      }
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      showToast(
        axiosError.response?.data?.message ||
          "No se pudo iniciar la conexión con Google",
        "error",
      );
    } finally {
      setGoogleWorking(false);
    }
  };

  const handleGoogleSyncNow = async () => {
    setGoogleWorking(true);
    try {
      await api.post("/classes/google/sync");
      showToast("Sincronización lanzada en segundo plano", "success");
      setTimeout(fetchGoogleStatus, 4000);
    } catch {
      showToast("No se pudo sincronizar", "error");
    } finally {
      setGoogleWorking(false);
    }
  };

  const handleGoogleDisconnect = async () => {
    const ok = await confirm({
      title: "Desconectar Google Calendar",
      message:
        "Las clases dejarán de sincronizarse. Los eventos ya creados se quedan en el calendario.",
      confirmText: "Desconectar",
      danger: true,
    });
    if (!ok) return;
    setGoogleWorking(true);
    try {
      await api.delete("/classes/google");
      showToast("Calendario desconectado", "success");
      fetchGoogleStatus();
    } catch {
      showToast("No se pudo desconectar", "error");
    } finally {
      setGoogleWorking(false);
    }
  };

  // ——— Calendario (excepciones por día: cerrar día / anular sesión) ———
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [calDays, setCalDays] = useState<Record<string, CalendarDayInfo>>({});
  const [calLoading, setCalLoading] = useState(false);
  const [calSelected, setCalSelected] = useState<string | null>(null);
  const [daySessions, setDaySessions] = useState<ClassSession[]>([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayWorking, setDayWorking] = useState(false);

  const calMonthStr = format(calMonth, "yyyy-MM");

  const fetchCalendar = useCallback(async () => {
    setCalLoading(true);
    try {
      const response = await api.get("/classes/calendar", {
        params: { month: calMonthStr },
      });
      const map: Record<string, CalendarDayInfo> = {};
      for (const d of response.data.data?.days || []) map[d.date] = d;
      setCalDays(map);
    } catch {
      showToast("No se pudo cargar el calendario de clases", "error");
    } finally {
      setCalLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calMonthStr]);

  useEffect(() => {
    if (tab === "calendar") fetchCalendar();
  }, [tab, fetchCalendar]);

  const fetchDaySessions = useCallback(async (dateStr: string) => {
    setDayLoading(true);
    try {
      const response = await api.get("/classes/agenda", {
        params: { date: dateStr },
      });
      setDaySessions(response.data.data || []);
    } catch {
      showToast("No se pudo cargar el día", "error");
    } finally {
      setDayLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCalDay = (dateStr: string) => {
    setCalSelected(dateStr);
    fetchDaySessions(dateStr);
  };

  const refreshAfterDayChange = (dateStr: string) => {
    fetchCalendar();
    fetchDaySessions(dateStr);
    fetchAgenda(); // por si la agenda tiene ese mismo día abierto
    fetchWeekInfo(); // y la tira semanal refleje el cierre al momento
  };

  const handleDayStatus = async (
    dateStr: string,
    close: boolean,
    force = false,
  ) => {
    setDayWorking(true);
    try {
      await api.post(close ? "/classes/day-close" : "/classes/day-open", {
        date: dateStr,
        ...(force ? { force: true } : {}),
      });
      showToast(close ? "Día cerrado" : "Día reabierto", "success");
      refreshAfterDayChange(dateStr);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      // 409 = hay gente apuntada: avisar y dejar forzar (las reservas no se
      // cancelan solas; el dueño avisa a los clientes).
      if (axiosError.response?.status === 409 && close && !force) {
        const ok = await confirm({
          title: "Cerrar el día",
          message: `${axiosError.response.data?.message || "Hay personas apuntadas ese día."} ¿Cerrarlo igualmente?`,
          confirmText: "Cerrar el día",
          danger: true,
        });
        if (ok) {
          await handleDayStatus(dateStr, close, true);
          return;
        }
      } else {
        showToast(
          axiosError.response?.data?.message || "No se pudo cambiar el día",
          "error",
        );
      }
    } finally {
      setDayWorking(false);
    }
  };

  const handleSessionToggle = async (
    templateId: string,
    dateStr: string,
    cancelled: boolean,
    force = false,
  ) => {
    setDayWorking(true);
    try {
      await api.post(`/classes/${templateId}/session-override`, {
        date: dateStr,
        cancelled,
        ...(force ? { force: true } : {}),
      });
      showToast(cancelled ? "Clase anulada" : "Clase reactivada", "success");
      refreshAfterDayChange(dateStr);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (axiosError.response?.status === 409 && cancelled && !force) {
        const ok = await confirm({
          title: "Anular la clase",
          message: `${axiosError.response.data?.message || "Hay personas apuntadas."} ¿Anularla igualmente?`,
          confirmText: "Anular",
          danger: true,
        });
        if (ok) {
          await handleSessionToggle(templateId, dateStr, cancelled, true);
          return;
        }
      } else {
        showToast(
          axiosError.response?.data?.message || "No se pudo cambiar la clase",
          "error",
        );
      }
    } finally {
      setDayWorking(false);
    }
  };

  // ——— Gestión de una reserva (modal al pulsar la fila de la agenda) ———
  const [manageBooking, setManageBooking] =
    useState<ManageBookingState | null>(null);
  const [manageForm, setManageForm] = useState({
    partySize: 1,
    date: "",
    templateId: "",
  });
  const [manageSessions, setManageSessions] = useState<ClassSession[]>([]);
  const [manageWorking, setManageWorking] = useState(false);

  // Clases del día elegido en el modal (para mover la reserva de clase/hora).
  const fetchManageSessions = useCallback(async (day: string) => {
    try {
      const response = await api.get("/classes/agenda", {
        params: { date: day },
      });
      setManageSessions(response.data.data || []);
    } catch {
      setManageSessions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openManageBooking = (s: ClassSession, a: ClassAttendee) => {
    setManageBooking({
      attendee: a,
      templateName: s.template.name,
      startTime: s.template.startTime,
      date: dateStr,
    });
    setManageForm({
      partySize: a.partySize,
      date: dateStr,
      templateId: s.template.id,
    });
    fetchManageSessions(dateStr);
  };

  const handleManageDateChange = (day: string) => {
    setManageForm((f) => ({ ...f, date: day, templateId: "" }));
    fetchManageSessions(day);
  };

  const refreshAfterBookingChange = () => {
    fetchAgenda();
    fetchWeekInfo();
    fetchCalendar();
  };

  const handleManageSave = async () => {
    if (!manageBooking) return;
    if (!manageForm.templateId) {
      showToast("Elige la clase de destino", "error");
      return;
    }
    setManageWorking(true);
    try {
      await api.patch(`/classes/bookings/${manageBooking.attendee.id}`, {
        partySize: manageForm.partySize,
        date: manageForm.date,
        templateId: manageForm.templateId,
      });
      showToast("Reserva actualizada", "success");
      setManageBooking(null);
      refreshAfterBookingChange();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      showToast(
        axiosError.response?.data?.message || "No se pudo modificar",
        "error",
      );
    } finally {
      setManageWorking(false);
    }
  };

  const handleManageCancel = async () => {
    if (!manageBooking) return;
    const name =
      manageBooking.attendee.bookingName ||
      `${manageBooking.attendee.customer.firstName} ${manageBooking.attendee.customer.lastName}`.trim();
    const ok = await confirm({
      title: "Cancelar reserva",
      message: `¿Cancelar la reserva de ${name} (${manageBooking.attendee.partySize} pax)? La plaza queda libre. No se avisa al cliente automáticamente.`,
      confirmText: "Cancelar reserva",
      danger: true,
    });
    if (!ok) return;
    setManageWorking(true);
    try {
      await api.post(
        `/classes/bookings/${manageBooking.attendee.id}/cancel`,
      );
      showToast("Reserva cancelada", "success");
      setManageBooking(null);
      refreshAfterBookingChange();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      showToast(
        axiosError.response?.data?.message || "No se pudo cancelar",
        "error",
      );
    } finally {
      setManageWorking(false);
    }
  };

  // Celdas del mes (lunes primero); null = hueco antes del día 1.
  const calCells: (number | null)[] = (() => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const jsDay = firstDay.getDay();
    const offset = jsDay === 0 ? 6 : jsDay - 1;
    return [
      ...(Array(offset).fill(null) as null[]),
      ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
  })();

  const allDayCancelled =
    daySessions.length > 0 && daySessions.every((s) => s.cancelled);

  const occupancyPill = (s: ClassSession) => {
    if (s.cancelled)
      return "bg-gray-200 text-gray-500";
    if (s.remaining <= 0) return "bg-red-100 text-red-700";
    if (s.remaining <= s.capacity * 0.25) return "bg-amber-100 text-amber-700";
    return "bg-green-100 text-green-700";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-6 h-6 text-primary-600" />
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Clases
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
            <button
              onClick={() => setTab("agenda")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                tab === "agenda"
                  ? "bg-primary-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Agenda
            </button>
            <button
              onClick={() => setTab("calendar")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                tab === "calendar"
                  ? "bg-primary-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Calendario
            </button>
            <button
              onClick={() => setTab("config")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                tab === "config"
                  ? "bg-primary-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              Clases
            </button>
          </div>
          {tab === "config" && (
            <button
              onClick={openCreate}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Nueva clase
            </button>
          )}
        </div>
      </div>

      {/* ——— AGENDA ——— */}
      {tab === "agenda" && (
        <div className="space-y-4">
          {/* Semana visible con los días con clase marcados: la navegación
              día a día a ciegas era ambigua (feedback 14-oct). */}
          <div className="bg-white rounded-xl border border-gray-200 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setAgendaDate((d) => addDays(d, -7))}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
                title="Semana anterior"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <span className="font-semibold text-gray-900 capitalize text-sm md:text-base">
                {format(agendaDate, "EEEE d 'de' MMMM", { locale: es })}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setAgendaDate(new Date())}
                  className="px-2.5 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md"
                >
                  Hoy
                </button>
                <button
                  onClick={() => setAgendaDate((d) => addDays(d, 7))}
                  className="p-1.5 hover:bg-gray-100 rounded-lg"
                  title="Semana siguiente"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: 7 }, (_, i) => {
                const monday = addDays(
                  agendaDate,
                  -((agendaDate.getDay() + 6) % 7),
                );
                const day = addDays(monday, i);
                const dateStr = format(day, "yyyy-MM-dd");
                const isSelected =
                  dateStr === format(agendaDate, "yyyy-MM-dd");
                // Datos reales si ya llegaron (anuladas incluidas);
                // mientras, conteo local por días de la semana.
                const info = weekInfo[dateStr];
                const localCount = templates.filter(
                  (t) => t.isActive && t.weekdays.includes(day.getDay()),
                ).length;
                const total = info ? info.total : localCount;
                const active = info ? info.total - info.cancelled : localCount;
                const closed = total > 0 && active === 0;
                const partial = total > 0 && active > 0 && active < total;
                return (
                  <button
                    key={i}
                    onClick={() => setAgendaDate(day)}
                    className={`rounded-lg py-1.5 text-center border transition-colors ${
                      isSelected
                        ? "bg-primary-600 border-primary-600 text-white"
                        : closed
                          ? "bg-red-50 border-red-200 text-gray-700 hover:bg-red-100"
                          : partial
                            ? "bg-amber-50 border-amber-200 text-gray-800 hover:bg-amber-100"
                            : total > 0
                              ? "bg-primary-50 border-primary-200 text-gray-800 hover:bg-primary-100"
                              : "bg-white border-gray-200 text-gray-400 hover:bg-gray-50"
                    }`}
                  >
                    <div className="text-[10px] uppercase font-medium">
                      {WEEKDAY_SHORT[day.getDay()]}
                    </div>
                    <div className="text-sm font-bold tabular-nums">
                      {format(day, "d")}
                    </div>
                    <div
                      className={`text-[10px] font-medium ${
                        isSelected
                          ? "text-white/80"
                          : closed
                            ? "text-red-600"
                            : partial
                              ? "text-amber-600"
                              : total > 0
                                ? "text-primary-600"
                                : "text-transparent"
                      }`}
                    >
                      {/* Alumnos del día (feedback 15-oct: el nº de clases es
                          obvio; el dato útil es cuánta gente viene). El color
                          ya marca el estado: ámbar = alguna clase anulada. */}
                      {closed ? (
                        "Cerrado"
                      ) : total > 0 ? (
                        info ? (
                          <span className="inline-flex items-center justify-center gap-0.5">
                            <Users className="w-2.5 h-2.5" />
                            {info.booked}
                          </span>
                        ) : (
                          `${total} clase${total > 1 ? "s" : ""}`
                        )
                      ) : (
                        "·"
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {agendaLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : sessions.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
              <GraduationCap className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              No hay clases programadas este día
            </div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.template.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0">
                    <span className="font-bold text-lg tabular-nums">
                      {s.template.startTime}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {s.template.emoji ? `${s.template.emoji} ` : ""}
                      {s.template.name}
                    </span>
                    {s.template.teacherName && (
                      <span className="text-sm text-gray-500">
                        Prof. {s.template.teacherName} ·{" "}
                        {s.template.durationMinutes} min
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-bold ${occupancyPill(s)}`}
                    >
                      {s.cancelled
                        ? "Sesión anulada"
                        : `${s.booked} / ${s.capacity} plazas`}
                    </span>
                    {!s.cancelled && s.remaining > 0 && (
                      <button
                        onClick={() => {
                          setAddingTo(
                            addingTo === s.template.id ? null : s.template.id,
                          );
                          setBookingForm({
                            firstName: "",
                            lastName: "",
                            phone: "",
                            partySize: 1,
                          });
                        }}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Apuntar
                      </button>
                    )}
                  </div>
                </div>

                {addingTo === s.template.id && (
                  <div className="px-4 py-3 bg-primary-50/50 border-b border-gray-100 grid grid-cols-2 md:grid-cols-5 gap-2">
                    <input
                      placeholder="Nombre *"
                      value={bookingForm.firstName}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          firstName: e.target.value,
                        })
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      placeholder="Apellidos"
                      value={bookingForm.lastName}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          lastName: e.target.value,
                        })
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      placeholder="Teléfono"
                      value={bookingForm.phone}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          phone: e.target.value,
                        })
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="number"
                      min={1}
                      max={s.remaining}
                      value={bookingForm.partySize}
                      onChange={(e) =>
                        setBookingForm({
                          ...bookingForm,
                          partySize: Math.max(
                            1,
                            parseInt(e.target.value, 10) || 1,
                          ),
                        })
                      }
                      title="Plazas"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      onClick={() => handleCreateBooking(s.template.id)}
                      disabled={bookingSubmitting}
                      className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                    >
                      {bookingSubmitting ? "Guardando…" : "Apuntar"}
                    </button>
                  </div>
                )}

                {s.attendees.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-gray-400">
                    Sin asistentes todavía
                  </div>
                ) : (
                  s.attendees.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => openManageBooking(s, a)}
                      title="Gestionar reserva"
                      className="w-full text-left flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-indigo-50/40 transition-colors"
                    >
                      <span className="font-medium text-gray-900 min-w-0">
                        {a.bookingName ||
                          `${a.customer.firstName} ${a.customer.lastName}`.trim()}
                        {a.bookedByCompany && (
                          <span className="ml-1.5 text-xs font-semibold text-indigo-600">
                            · {a.bookedByCompany}
                          </span>
                        )}
                        <span className="ml-2 text-xs text-gray-400">
                          · {a.source === "widget" ? "Widget" : "Panel"}
                        </span>
                      </span>
                      <span className="flex items-center gap-4 text-sm text-gray-500 tabular-nums">
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {a.partySize}
                        </span>
                        {a.customer.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {a.customer.phone}
                          </span>
                        )}
                        <Pencil className="w-3.5 h-3.5 text-gray-300" />
                      </span>
                    </button>
                  ))
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ——— CONFIGURACIÓN ——— */}
      {tab === "config" && (
        <div className="space-y-4">
          {configLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : templates.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-500">
              <GraduationCap className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="font-medium text-gray-700 mb-1">
                Aún no hay clases
              </p>
              <p className="text-sm">
                Crea la primera con «Nueva clase»: días, hora, aforo y
                profesor.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.map((t) => (
                <div
                  key={t.id}
                  className={`bg-white rounded-xl border border-gray-200 p-4 space-y-2 ${
                    t.isActive ? "" : "opacity-60"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-gray-900 min-w-0 truncate">
                      {t.emoji ? `${t.emoji} ` : ""}
                      {t.name}
                    </span>
                    <button
                      onClick={() => handleToggleActive(t)}
                      className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                        t.isActive ? "bg-green-500" : "bg-gray-300"
                      }`}
                      title={t.isActive ? "Desactivar" : "Activar"}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                          t.isActive ? "right-0.5" : "left-0.5"
                        }`}
                      />
                    </button>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    {t.teacherName && <p>Prof. {t.teacherName}</p>}
                    <p className="tabular-nums">
                      {formatWeekdays(t.weekdays)} · {t.startTime} ·{" "}
                      {t.durationMinutes} min
                    </p>
                    <p>
                      Aforo:{" "}
                      <span className="font-semibold">{t.capacity} plazas</span>
                    </p>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => openEdit(t)}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                    >
                      <Pencil className="w-3 h-3" />
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(t)}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-red-50 hover:bg-red-100 text-red-600 rounded-lg"
                    >
                      <Trash2 className="w-3 h-3" />
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Conexión con Google Calendar (una por sede) */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-gray-900 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-primary-600" />
                  Google Calendar
                </p>
                {googleStatus?.connected ? (
                  <div className="text-sm text-gray-600 mt-1 space-y-1">
                    <p>
                      Conectado
                      {googleStatus.googleEmail
                        ? ` como ${googleStatus.googleEmail}`
                        : ""}
                      . Cada clase con reservas aparece en el calendario
                      «Clases — {googleStatus.locationName}» con el cupo y la
                      lista de asistentes.
                    </p>
                    {googleStatus.syncStatus === "error" &&
                      googleStatus.lastError && (
                        <p className="text-xs text-red-600">
                          Último error: {googleStatus.lastError}
                        </p>
                      )}
                    {googleStatus.lastSyncAt && (
                      <p className="text-xs text-gray-400">
                        Última sincronización:{" "}
                        {format(
                          new Date(googleStatus.lastSyncAt),
                          "d MMM HH:mm",
                          { locale: es },
                        )}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 mt-1">
                    Conecta una cuenta de Google y cada clase con reservas
                    aparecerá en su calendario con el cupo y la lista de
                    asistentes, actualizados solos.
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {googleStatus?.connected ? (
                  <>
                    <button
                      onClick={handleGoogleSyncNow}
                      disabled={googleWorking}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50"
                    >
                      <RefreshCw
                        className={`w-4 h-4 ${googleWorking ? "animate-spin" : ""}`}
                      />
                      Sincronizar
                    </button>
                    <button
                      onClick={handleGoogleDisconnect}
                      disabled={googleWorking}
                      className="px-3 py-2 text-sm font-medium bg-red-50 hover:bg-red-100 text-red-600 rounded-lg disabled:opacity-50"
                    >
                      Desconectar
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleGoogleConnect}
                    disabled={googleWorking}
                    className="px-3 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50"
                  >
                    Conectar con Google
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ——— CALENDARIO (excepciones: cerrar día / anular clases) ——— */}
      {tab === "calendar" && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() =>
                  setCalMonth(
                    (m) => new Date(m.getFullYear(), m.getMonth() - 1, 1),
                  )
                }
                className="p-1.5 hover:bg-gray-100 rounded-lg"
                title="Mes anterior"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <span className="font-semibold text-gray-900 capitalize">
                {format(calMonth, "MMMM yyyy", { locale: es })}
              </span>
              <button
                onClick={() =>
                  setCalMonth(
                    (m) => new Date(m.getFullYear(), m.getMonth() + 1, 1),
                  )
                }
                className="p-1.5 hover:bg-gray-100 rounded-lg"
                title="Mes siguiente"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            {calLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-1">
                  {["L", "M", "X", "J", "V", "S", "D"].map((d, i) => (
                    <div key={`${d}${i}`}>{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {calCells.map((day, i) => {
                    if (day === null) return <div key={`empty-${i}`} />;
                    const dateStr = `${calMonthStr}-${String(day).padStart(2, "0")}`;
                    const info = calDays[dateStr];
                    const closed = info && info.cancelled === info.total;
                    const partial =
                      info && info.cancelled > 0 && info.cancelled < info.total;
                    return (
                      <button
                        key={dateStr}
                        onClick={() => info && openCalDay(dateStr)}
                        disabled={!info}
                        className={`min-h-[52px] md:min-h-[64px] rounded-lg border p-1 flex flex-col items-center justify-start gap-0.5 text-left ${
                          info
                            ? "border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/40 cursor-pointer"
                            : "border-transparent bg-gray-50 text-gray-300"
                        }`}
                      >
                        <span className="text-xs font-medium text-gray-700">
                          {day}
                        </span>
                        {info && (
                          <span
                            className={`px-1 py-0.5 rounded text-[10px] font-semibold leading-none ${
                              closed
                                ? "bg-red-100 text-red-700"
                                : partial
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-green-100 text-green-700"
                            }`}
                          >
                            {closed
                              ? "Cerrado"
                              : partial
                                ? `${info.total - info.cancelled}/${info.total}`
                                : `${info.total} ${info.total === 1 ? "clase" : "clases"}`}
                          </span>
                        )}
                        {info && info.booked > 0 && !closed && (
                          <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                            <Users className="w-2.5 h-2.5" />
                            {info.booked}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <p className="text-sm text-gray-500 px-1">
            Pulsa un día con clases para cerrarlo entero (festivo, cierre
            puntual) o anular clases sueltas. Si hay gente apuntada, te
            avisará antes.
          </p>
        </div>
      )}

      {/* ——— Modal de gestión de una reserva de clase ——— */}
      {manageBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[60] md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Gestionar reserva</h2>
              <button
                onClick={() => setManageBooking(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Quién */}
              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <p className="font-semibold text-gray-900">
                  {manageBooking.attendee.bookingName ||
                    `${manageBooking.attendee.customer.firstName} ${manageBooking.attendee.customer.lastName}`.trim()}
                  {manageBooking.attendee.bookedByCompany && (
                    <span className="ml-1.5 text-sm font-semibold text-indigo-600">
                      · {manageBooking.attendee.bookedByCompany}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {manageBooking.startTime} · {manageBooking.templateName} ·{" "}
                  {format(
                    new Date(`${manageBooking.date}T00:00:00`),
                    "d 'de' MMMM",
                    { locale: es },
                  )}
                  {manageBooking.attendee.customer.phone
                    ? ` · ${manageBooking.attendee.customer.phone}`
                    : ""}
                  {" · "}
                  {manageBooking.attendee.source === "widget"
                    ? "Widget"
                    : "Panel"}
                </p>
              </div>

              {/* Personas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personas
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() =>
                      setManageForm((f) => ({
                        ...f,
                        partySize: Math.max(1, f.partySize - 1),
                      }))
                    }
                    className="w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    <span className="text-lg leading-none">−</span>
                  </button>
                  <span className="text-lg font-bold tabular-nums w-10 text-center">
                    {manageForm.partySize}
                  </span>
                  <button
                    onClick={() =>
                      setManageForm((f) => ({
                        ...f,
                        partySize: Math.min(100, f.partySize + 1),
                      }))
                    }
                    className="w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                  >
                    <span className="text-lg leading-none">+</span>
                  </button>
                </div>
              </div>

              {/* Mover de día / clase */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Día
                  </label>
                  <input
                    type="date"
                    value={manageForm.date}
                    onChange={(e) => handleManageDateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clase
                  </label>
                  <select
                    value={manageForm.templateId}
                    onChange={(e) =>
                      setManageForm((f) => ({
                        ...f,
                        templateId: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Elige la clase…</option>
                    {manageSessions.map((ms) => (
                      <option
                        key={ms.template.id}
                        value={ms.template.id}
                        disabled={ms.cancelled}
                      >
                        {ms.template.startTime} — {ms.template.name}
                        {ms.cancelled
                          ? " (anulada)"
                          : ` (quedan ${ms.remaining})`}
                      </option>
                    ))}
                  </select>
                  {manageSessions.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Ese día no hay clases.
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={handleManageSave}
                disabled={manageWorking}
                className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl disabled:opacity-50"
              >
                Guardar cambios
              </button>
              <button
                onClick={handleManageCancel}
                disabled={manageWorking}
                className="w-full py-2.5 bg-red-50 hover:bg-red-100 text-red-600 font-medium rounded-xl disabled:opacity-50"
              >
                Cancelar reserva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ——— Modal del día (cerrar día / anular sesiones) ——— */}
      {calSelected && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[60] md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <h2 className="font-bold text-gray-900 capitalize">
                {format(new Date(`${calSelected}T00:00:00`), "EEEE d 'de' MMMM", {
                  locale: es,
                })}
              </h2>
              <button
                onClick={() => setCalSelected(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {dayLoading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600"></div>
                </div>
              ) : daySessions.length === 0 ? (
                <p className="text-sm text-gray-500">Ese día no hay clases.</p>
              ) : (
                <>
                  <button
                    onClick={() =>
                      handleDayStatus(calSelected, !allDayCancelled)
                    }
                    disabled={dayWorking}
                    className={`w-full px-3 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 ${
                      allDayCancelled
                        ? "bg-green-50 hover:bg-green-100 text-green-700"
                        : "bg-red-50 hover:bg-red-100 text-red-600"
                    }`}
                  >
                    {allDayCancelled
                      ? "Reabrir el día (reactivar todas las clases)"
                      : "Cerrar el día (anular todas las clases)"}
                  </button>
                  <div className="space-y-2">
                    {daySessions.map((s) => (
                      <div
                        key={s.template.id}
                        className={`flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2 ${
                          s.cancelled ? "opacity-60" : ""
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {s.template.startTime} · {s.template.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {s.cancelled
                              ? "Anulada"
                              : `${s.booked}/${s.capacity} plazas ocupadas`}
                            {s.template.teacherName
                              ? ` · ${s.template.teacherName}`
                              : ""}
                          </p>
                        </div>
                        <button
                          onClick={() =>
                            handleSessionToggle(
                              s.template.id,
                              calSelected,
                              !s.cancelled,
                            )
                          }
                          disabled={dayWorking}
                          className={`px-2.5 py-1.5 text-xs font-medium rounded-lg flex-shrink-0 disabled:opacity-50 ${
                            s.cancelled
                              ? "bg-green-50 hover:bg-green-100 text-green-700"
                              : "bg-red-50 hover:bg-red-100 text-red-600"
                          }`}
                        >
                          {s.cancelled ? "Reactivar" : "Anular"}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ——— Modal crear/editar ——— */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-[60] md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">
                {editingId ? "Editar clase" : "Nueva clase"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Flamenco"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profesor/a
                </label>
                <input
                  value={form.teacherName}
                  onChange={(e) =>
                    setForm({ ...form, teacherName: e.target.value })
                  }
                  placeholder="Juan Cortés"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Días de la semana *
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAY_ORDER.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          weekdays: form.weekdays.includes(d)
                            ? form.weekdays.filter((x) => x !== d)
                            : [...form.weekdays, d],
                        })
                      }
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                        form.weekdays.includes(d)
                          ? "bg-primary-600 border-primary-600 text-white"
                          : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {WEEKDAY_SHORT[d]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora de inicio *
                  </label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) =>
                      setForm({ ...form, startTime: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                {!editingId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hora de fin{" "}
                      <span className="font-normal text-gray-400">
                        (para crear por franjas)
                      </span>
                    </label>
                    <input
                      type="time"
                      value={form.endTime}
                      onChange={(e) =>
                        setForm({ ...form, endTime: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duración (min)
                  </label>
                  <input
                    type="number"
                    min={15}
                    max={480}
                    value={form.durationMinutes}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        durationMinutes: parseInt(e.target.value, 10) || 60,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aforo por clase *
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={form.capacity}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        capacity: parseInt(e.target.value, 10) || 1,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              {!editingId && form.endTime && (
                <div
                  className={`text-sm rounded-lg px-3 py-2 ${
                    slotStarts(
                      form.startTime,
                      form.endTime,
                      form.durationMinutes,
                    ).length > 0
                      ? "bg-primary-50 text-primary-700"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {(() => {
                    const starts = slotStarts(
                      form.startTime,
                      form.endTime,
                      form.durationMinutes,
                    );
                    return starts.length > 0
                      ? `Se crearán ${starts.length} clase(s): ${starts.join(" · ")}`
                      : "La hora de fin no deja sitio a ninguna clase completa";
                  })()}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={formSubmitting}
                  className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg disabled:opacity-50"
                >
                  {formSubmitting
                    ? "Guardando…"
                    : editingId
                      ? "Guardar cambios"
                      : "Crear clase"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
